import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Target, Trophy, Flame, ListChecks } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AvatarUploader } from "../components/common/Avatar";

export default function ProfilePage() {
  const { user, setAuthOpen, refresh } = useAuth();
  const nav = useNavigate();
  const [predictions, setPredictions] = useState([]);

  useEffect(() => {
    if (!user) return;
    api.get("/predictions/me").then((r) => setPredictions(r.data));
  }, [user]);

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-24 text-center">
        <div className="tile p-10">
          <h2 className="font-display font-black text-3xl uppercase">Sign in to view your profile</h2>
          <p className="text-slate-400 mt-2">Your prediction vault, streaks and rank live here.</p>
          <button className="btn-primary mt-6" onClick={() => setAuthOpen(true)} data-testid="profile-signin-cta">Sign in</button>
        </div>
      </div>
    );
  }

  const accuracy = user.total_predictions > 0 ? Math.round((user.correct_predictions / user.total_predictions) * 100) : 0;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
      <div className="tile p-8 grain mb-8">
        <div className="flex items-center gap-5 flex-wrap">
          <AvatarUploader user={user} onChange={() => refresh?.()} />
          <div>
            <div className="section-label">Predictor</div>
            <div className="font-display font-black uppercase text-4xl tracking-tight mt-1">{user.name}</div>
            <div className="text-slate-400 text-sm">{user.email}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <StatCard icon={<Trophy className="w-5 h-5 text-amber-400" />} label="Total points" value={user.points} testid="profile-total-points" />
        <StatCard icon={<Target className="w-5 h-5 text-emerald-400" />} label="Accuracy" value={`${accuracy}%`} testid="profile-accuracy-stat" />
        <StatCard icon={<Flame className="w-5 h-5 text-rose-400" />} label="Current streak" value={user.streak} testid="profile-streak" />
        <StatCard icon={<ListChecks className="w-5 h-5 text-sky-400" />} label="Predictions" value={`${user.correct_predictions}/${user.total_predictions}`} testid="profile-predictions-count" />
      </div>

      <div className="section-label mb-2">Prediction vault</div>
      <h2 className="font-display font-extrabold uppercase text-3xl mb-5">Your calls</h2>

      <div className="tile p-0 overflow-hidden">
        {predictions.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-slate-400">No predictions yet. <Link to="/" className="text-amber-400 hover:underline">Enter a live match</Link> to make your first call.</p>
          </div>
        ) : predictions.map((p) => (
          <div key={p.id} className="grid grid-cols-12 items-center px-5 py-4 border-b border-white/5 gap-3" data-testid={`prediction-row-${p.id}`}>
            <div className="col-span-3 sm:col-span-2 font-display font-bold uppercase">{p.match_title}</div>
            <div className="col-span-3 sm:col-span-2 text-xs">
              <div className="section-label">Over</div>
              <div className="font-mono-data text-lg">#{p.over_number}</div>
            </div>
            <div className="col-span-3 sm:col-span-3 text-xs">
              <div className="section-label">Call</div>
              <div className="font-mono-data">{p.runs_bucket} runs • {p.wicket_prediction ? "W: Yes" : "W: No"}</div>
            </div>
            <div className="col-span-3 sm:col-span-2 text-xs">
              <div className="section-label">Actual</div>
              <div className="font-mono-data">{p.status === "pending" ? "—" : `${p.actual_bucket ?? "-"} • ${p.actual_wicket ? "W" : "no W"}`}</div>
            </div>
            <div className="col-span-12 sm:col-span-3 text-right">
              <StatusBadge status={p.status} points={p.points_earned} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, testid }) {
  return (
    <div className="tile p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">{icon}<span className="section-label">{label}</span></div>
      <div className="metric-value text-3xl">{value}</div>
    </div>
  );
}

function StatusBadge({ status, points }) {
  if (status === "won") return <div className="pill bg-emerald-500/15 text-emerald-300 border border-emerald-500/40">+{points} pts</div>;
  if (status === "lost") return <div className="pill bg-rose-500/15 text-rose-300 border border-rose-500/40">Missed</div>;
  return <div className="pill bg-sky-500/15 text-sky-300 border border-sky-500/30">Pending</div>;
}
